/**
 * Sales orders — `/sales/orders`.
 *
 * A cursor list over `GET /api/v1/sales-orders` with the contract's own
 * filters. The endpoint orders by created_at,id descending and publishes no
 * sort parameter, so this table offers no sortable headers rather than
 * pretending to a control the server does not have.
 *
 * The customer column is missing on purpose: the list projection carries no
 * customer name. Only the order detail can reveal one, behind its own audited
 * permission.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@erp/ui";

import { DataTable, dateTimeCell, textCell } from "@/components/data-table";
import { MoneyDisplay } from "@/components/money/money-display";
import { useChannelNames } from "@/features/channels/queries";

import { SALES_ORDER_STATUSES, type SalesOrderFilters, type SalesOrderSummary } from "../api";
import { useSalesOrderList } from "../queries";
import { SalesOrderStatusBadge } from "../components/status-badge";

const ALL = "__all__";

export interface OrdersPageProps {
  onOpenOrder: (salesOrderId: string) => void;
  /** `channels.channel.read` — without it the channel column shows the id. */
  canReadChannels?: boolean;
  /** Page size override (tests exercise cursor paging with a small limit). */
  pageLimit?: number;
}

export function OrdersPage({ onOpenOrder, canReadChannels = true, pageLimit }: OrdersPageProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<string>(ALL);
  const [channelId, setChannelId] = useState<string>(ALL);
  const [externalRef, setExternalRef] = useState("");

  const { channelName, channels } = useChannelNames(canReadChannels);

  const filters = useMemo<SalesOrderFilters>(() => {
    const built: SalesOrderFilters = {};
    if (status !== ALL) built.status = status as SalesOrderFilters["status"];
    if (channelId !== ALL) built.channel_id = channelId;
    if (externalRef !== "") built.external_ref = externalRef;
    return built;
  }, [status, channelId, externalRef]);

  const query = useSalesOrderList(filters, pageLimit);

  const columns = useMemo<ColumnDef<SalesOrderSummary, unknown>[]>(
    () => [
      {
        id: "order_number",
        accessorKey: "order_number",
        header: t("sales.orders.columns.number", "Order"),
        cell: ({ row }) => (
          <span dir="ltr" className="font-mono text-xs">
            {row.original.order_number ?? row.original.external_ref ?? "—"}
          </span>
        ),
      },
      {
        id: "status",
        accessorKey: "status",
        header: t("sales.orders.columns.status", "Status"),
        cell: ({ row }) => <SalesOrderStatusBadge status={row.original.status} />,
      },
      {
        id: "channel_id",
        accessorKey: "channel_id",
        header: t("sales.orders.columns.channel", "Channel"),
        cell: ({ row }) =>
          textCell(
            row.original.channel_id === undefined
              ? t("sales.orders.internal", "Internal")
              : channelName(row.original.channel_id),
          ),
      },
      {
        id: "placed_at",
        accessorKey: "placed_at",
        header: t("sales.orders.columns.placed", "Placed"),
        cell: ({ row }) =>
          row.original.placed_at === undefined
            ? textCell("—")
            : dateTimeCell(row.original.placed_at),
      },
      {
        id: "grand_total",
        accessorKey: "grand_total",
        header: t("sales.orders.columns.total", "Total"),
        meta: { numeric: true },
        cell: ({ row }) => <MoneyDisplay value={row.original.grand_total} />,
      },
    ],
    [t, channelName],
  );

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="space-y-1">
        <h1 className="text-xl font-semibold tracking-tight">
          {t("sales.orders.title", "Sales orders")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t(
            "sales.orders.subtitle",
            "Orders drafted here or imported from a channel, newest first.",
          )}
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="sales-status">{t("sales.orders.columns.status", "Status")}</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger id="sales-status" className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("filters.all", "All")}</SelectItem>
              {SALES_ORDER_STATUSES.map((value) => (
                <SelectItem key={value} value={value}>
                  {t(`sales.status.${value}`, value)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {canReadChannels && channels.length > 0 ? (
          <div className="space-y-1.5">
            <Label htmlFor="sales-channel">{t("sales.orders.columns.channel", "Channel")}</Label>
            <Select value={channelId} onValueChange={setChannelId}>
              <SelectTrigger id="sales-channel" className="w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t("filters.all", "All")}</SelectItem>
                {channels.map((channel) => (
                  <SelectItem key={channel.id} value={channel.id}>
                    {channel.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}

        <div className="space-y-1.5">
          <Label htmlFor="sales-external-ref">
            {t("sales.orders.filters.externalRef", "Source reference")}
          </Label>
          <Input
            id="sales-external-ref"
            dir="ltr"
            className="w-56 font-mono"
            value={externalRef}
            onChange={(event) => setExternalRef(event.target.value)}
          />
        </div>
      </div>

      <DataTable
        columns={columns}
        query={query}
        getRowId={(row) => row.id}
        onRowClick={(row) => onOpenOrder(row.id)}
        emptyMessage={t("sales.orders.empty", "No sales orders match these filters.")}
        storageKey="sales.orders.columns"
      />
    </div>
  );
}
